import React, { useState, useEffect } from 'react';
import { Sparkles, Clock, MessageCircle, BookOpen, ChevronRight, Loader, CheckCircle, Trash2, X, AlertTriangle, Crown, Menu, Headphones } from 'lucide-react';
import { useSessionLimit } from '../shared/hooks/useSessionLimit';
import { PremiumUpgradeModal } from '../Components/PremiumSystem';
import { getCurrentUser } from '../shared/api/auth';
import { fetchUserSessions, deleteSession, getSessionById } from '../shared/api/sessions';

// ✅ Import centralized theme
import { gradients, buttonStyles, cardStyles } from '../shared/config/theme';
import { getJourneyConfig } from '../shared/constants/journeyConfig';
import { getTimeGreeting } from '../shared/utils/dateHelpers';
// ✅ Import centralized components
import Header from '../shared/components/Header';

const SessionHistory = ({ onStartNewSession, user, userProfile,streakCount,onNavigate, onNavigateToMore, onNavigateToProgress, onNavigateToLibrary, isAuthenticated }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSession, setExpandedSession] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ show: false, sessionIds: [] });
  const [deleting, setDeleting] = useState(false);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const {
    sessionCount,
    isPremium,
    sessionsRemaining,
    limitEnabled,
    FREE_SESSION_LIMIT
  } = useSessionLimit(user?.id);

// ✅ Add these navigation handlers
const handleMenuClick = () => {
  console.log('Menu clicked - navigating to more');
  if (onNavigate) {
    onNavigate('more');
  } else {
    console.error('onNavigate function not provided');
  }
};

const handleProgressClick = () => {
  console.log('Progress clicked - navigating to progress');
  if (onNavigate) {
    onNavigate('progress');
  } else {
    console.error('onNavigate function not provided');
  }
};


  const getUserName = () => {
    if (userProfile?.full_name) return userProfile.full_name;
    if (user?.user_metadata?.full_name) return user.user_metadata.full_name;
    return 'Friend';
  };

  const getUserInitial = () => {
    const name = getUserName();
    return name.charAt(0).toUpperCase();
  };

  useEffect(() => {
    fetchSessionsData();
  }, []);

  const fetchSessionsData = async () => {
    try {
      setLoading(true);
      const { user: currentUser, error: userError } = await getCurrentUser();
      if (userError || !currentUser) {
        setLoading(false);
        return;
      }

      const { sessions: fetchedSessions, error } = await fetchUserSessions(currentUser.id);

      if (error) {
        console.error('Error fetching sessions:', error);
        throw error;
      }
      
      console.log('✅ Fetched sessions:', fetchedSessions);
      setSessions(fetchedSessions || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      setLoading(false);
    }
  };

  const handleDelete = async (sessionIds) => {
    try {
      setDeleting(true);
      for (const sessionId of sessionIds) {
        const { error } = await deleteSession(sessionId);
        if (error) {
          console.error('Error deleting session:', sessionId, error);
        }
      }
      setSessions(prevSessions => prevSessions.filter(s => !sessionIds.includes(s.id)));
      setDeleteModal({ show: false, sessionIds: [] });
      setSelectedSessions([]);
      setSelectMode(false);
      setDeleting(false);
    } catch (error) {
      console.error('Error deleting sessions:', error);
      setDeleting(false);
      alert(`Failed to delete sessions: ${error.message}`);
    }
  };

  const handleResumeSession = async (session) => {
    try {
      console.log('🔄 RESUME: Starting with session:', session.id);
      
      let journeyIdToUse = null;
      
      if (session.journey_id && session.journey_id !== 'undefined' && session.journey_id !== 'null') {
        journeyIdToUse = session.journey_id;
        console.log('✅ RESUME: Found journey_id directly:', journeyIdToUse);
      }
      
      if (!journeyIdToUse && session.journeys?.id) {
        journeyIdToUse = session.journeys.id;
        console.log('✅ RESUME: Found journey_id from nested object:', journeyIdToUse);
      }
      
      if (!journeyIdToUse) {
        console.log('⚠️ RESUME: Fetching journey_id from database...');
        const { session: dbSession, error: dbError } = await getSessionById(session.id);
        
        if (dbError) {
          console.error('❌ RESUME: Database error:', dbError);
          alert('Failed to load session. Please try again.');
          return;
        }
        
        journeyIdToUse = dbSession?.journey_id;
        console.log('✅ RESUME: Fetched journey_id from DB:', journeyIdToUse);
      }
      
      if (!journeyIdToUse || journeyIdToUse === 'undefined' || journeyIdToUse === 'null') {
        console.error('❌ RESUME: No valid journey_id found!');
        alert('This session is missing journey information. Please start a new session.');
        return;
      }
      
      console.log('✅ RESUME: Final journey_id:', journeyIdToUse);
      console.log('✅ RESUME: Session step:', session.current_step || 'chat');
      
      sessionStorage.clear();
      
      const journeyData = session.journeys || { 
        id: journeyIdToUse,
        title: 'Journey',
        category: session.category || 'confidence'
      };

      sessionStorage.setItem('sessionId', session.id);
      sessionStorage.setItem('journeyId', journeyIdToUse);
      sessionStorage.setItem('journeyData', JSON.stringify(journeyData));
      sessionStorage.setItem('currentStep', session.current_step || 'chat');
      sessionStorage.setItem('resumedSession', 'true');
      
      console.log('✅ RESUME: All sessionStorage set:', {
        sessionId: session.id,
        journeyId: journeyIdToUse,
        journeyData: journeyData,
        currentStep: session.current_step || 'chat'
      });
      
      window.location.href = '/journey';
    } catch (error) {
      console.error('❌ RESUME: Critical error:', error);
      alert('Failed to resume session. Please try again or start a new session.');
    }
  };

  const toggleSessionSelection = (sessionId, e) => {
    if (e) {
      e.stopPropagation();
    }
    setSelectedSessions(prev => 
      prev.includes(sessionId) 
        ? prev.filter(id => id !== sessionId)
        : [...prev, sessionId]
    );
  };

  const hasMeditation = (session) => {
    return session.meditation_completed === true || 
           (session.meditation_technique && session.meditation_technique !== null);
  };

  const getMeditationTechnique = (session) => {
    if (!session.meditation_technique) return 'Meditation';
    
    const techniques = {
      'breathing': 'Breathing Exercise',
      'body-scan': 'Body Scan',
      'visualization': 'Visualization',
      'mantra': 'Mantra Meditation',
      'loving-kindness': 'Loving-Kindness'
    };
    
    return techniques[session.meditation_technique] || 'Meditation';
  };

  const renderInProgressSessions = () => {
    const inProgress = sessions.filter(s => !s.completed_at);
    
    if (inProgress.length === 0) return null;

    return (
      <div className="mb-8">
        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-orange-500" />
          In Progress
        </h2>
        <div className="space-y-4">
          {inProgress.map(session => {
            const journey = session.journeys;
            if (!journey) return null;
            
            const config = getJourneyConfig(journey.category) || {};
            const isSelected = selectedSessions.includes(session.id);
            
            return (
              <div 
                key={session.id}
                className={`${cardStyles.default.background} ${cardStyles.default.border} ${cardStyles.default.rounded} ${cardStyles.default.shadow} ${cardStyles.default.padding} hover:shadow-lg transition-all ${
                  isSelected ? 'ring-2 ring-orange-500' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    {/* ✅ Checkbox for select mode */}
                    {selectMode && (
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSessionSelection(session.id, e);
                        }}
                        className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center cursor-pointer transition-all ${
                          isSelected 
                            ? 'bg-orange-500 border-orange-500' 
                            : 'bg-white border-gray-300'
                        }`}
                      >
                        {isSelected && (
                          <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    )}
                    <div className={`w-12 h-12 bg-gradient-to-br ${config.gradient || 'from-orange-400 to-orange-600'} rounded-xl flex items-center justify-center flex-shrink-0 text-white text-xl`}>
                      {config.emoji || '✨'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-800 text-lg mb-1">{journey.title}</h3>
                      <p className="text-sm text-gray-500">
                        {new Date(session.created_at).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!selectMode && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteModal({ show: true, sessionIds: [session.id] });
                        }}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                    {!selectMode && (
                      <button
                        onClick={() => handleResumeSession(session)}
                        className={`${buttonStyles.primary.base} ${buttonStyles.primary.hover} ${buttonStyles.primary.active} ${buttonStyles.primary.transition} ${buttonStyles.primary.rounded} ${buttonStyles.primary.shadow} py-2 px-6 text-sm flex items-center gap-2`}
                      >
                        Continue
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderCompletedSessions = () => {
    const completed = sessions.filter(s => s.completed_at);

    return (
      <div className="space-y-4">
        {completed.map(session => {
          const journey = session.journeys;
          if (!journey) return null;

          const config = getJourneyConfig(journey.category) || {};
          const isExpanded = expandedSession === session.id;
          const isSelected = selectedSessions.includes(session.id);

          return (
            <div 
              key={session.id}
              className={`${cardStyles.default.background} ${cardStyles.default.border} ${cardStyles.default.rounded} overflow-hidden transition-all ${
                isSelected ? 'ring-2 ring-orange-500' : ''
              } ${isExpanded ? 'shadow-xl' : cardStyles.default.shadow}`}
            >
              <div className={`${cardStyles.default.padding}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    {selectMode && (
                      <div 
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSessionSelection(session.id, e);
                        }}
                        className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center cursor-pointer transition-all ${
                          isSelected 
                            ? 'bg-orange-500 border-orange-500' 
                            : 'bg-white border-gray-300'
                        }`}
                      >
                        {isSelected && (
                          <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    )}
                    <div className={`w-12 h-12 bg-gradient-to-br ${config.gradient || 'from-orange-400 to-orange-600'} rounded-xl flex items-center justify-center flex-shrink-0 text-white text-xl`}>
                      {config.emoji || '✨'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-800 text-lg truncate">{journey.title}</h3>
                      <p className="text-sm text-gray-500">
                        {new Date(session.completed_at).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {!selectMode && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteModal({ show: true, sessionIds: [session.id] });
                        }}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedSession(isExpanded ? null : session.id);
                      }}
                      className={`p-2 hover:bg-gray-100 rounded-lg transition-all ${isExpanded ? 'rotate-90' : ''}`}
                    >
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <div className={`flex items-center gap-1.5 ${config.lightColor || 'bg-orange-100'} ${config.textColor || 'text-orange-700'} px-3 py-1.5 rounded-full text-xs font-medium`}>
                    <MessageCircle className="w-3.5 h-3.5" />
                    AI Chat
                  </div>
                  <div className={`flex items-center gap-1.5 ${config.lightColor || 'bg-orange-100'} ${config.textColor || 'text-orange-700'} px-3 py-1.5 rounded-full text-xs font-medium`}>
                    <BookOpen className="w-3.5 h-3.5" />
                    Journal
                  </div>
                  {hasMeditation(session) && (
                    <div className={`flex items-center gap-1.5 ${config.lightColor || 'bg-orange-100'} ${config.textColor || 'text-orange-700'} px-3 py-1.5 rounded-full text-xs font-medium`}>
                      <Headphones className="w-3.5 h-3.5" />
                      {getMeditationTechnique(session)}
                    </div>
                  )}
                </div>
              </div>

              {isExpanded && (
                <div className="border-t border-gray-100 bg-gray-50/50 p-6">
                  <div className="space-y-6">
                    {session.ai_response && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <div className={`w-8 h-8 bg-gradient-to-br ${config.gradient || 'from-orange-400 to-orange-600'} rounded-lg flex items-center justify-center`}>
                            <MessageCircle className="w-4 h-4 text-white" />
                          </div>
                          <h4 className="font-semibold text-gray-800">AI Guidance</h4>
                        </div>
                        <div className="bg-white rounded-xl p-4 border border-gray-200">
                          <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
                            {session.ai_response.substring(0, 300)}
                            {session.ai_response.length > 300 && '...'}
                          </p>
                        </div>
                      </div>
                    )}

                    {session.journal_entry && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <div className={`w-8 h-8 bg-gradient-to-br ${config.gradient || 'from-orange-400 to-orange-600'} rounded-lg flex items-center justify-center`}>
                            <BookOpen className="w-4 h-4 text-white" />
                          </div>
                          <h4 className="font-semibold text-gray-800">Your Reflection</h4>
                        </div>
                        <div className="bg-white rounded-xl p-4 border border-gray-200">
                          <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
                            {session.journal_entry.substring(0, 300)}
                            {session.journal_entry.length > 300 && '...'}
                          </p>
                        </div>
                      </div>
                    )}

                    {hasMeditation(session) && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <div className={`w-8 h-8 bg-gradient-to-br ${config.gradient || 'from-orange-400 to-orange-600'} rounded-lg flex items-center justify-center`}>
                            <Headphones className="w-4 h-4 text-white" />
                          </div>
                          <h4 className="font-semibold text-gray-800">Meditation</h4>
                        </div>
                        <div className="bg-white rounded-xl p-4 border border-gray-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-gray-700 font-medium">{getMeditationTechnique(session)}</p>
                              {session.meditation_duration && (
                                <p className="text-sm text-gray-500 mt-1">
                                  Duration: {Math.floor(session.meditation_duration / 60)} minutes
                                </p>
                              )}
                            </div>
                            {session.meditation_completed && (
                              <CheckCircle className="w-6 h-6 text-green-500" />
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center bg-gradient-to-br ${gradients.background}`}>
        <div className="text-center">
          <Loader className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your sessions...</p>
        </div>
      </div>
    );
  }

  const completedSessions = sessions.filter(s => s.completed_at);
  const canStartNewSession = !limitEnabled || sessionsRemaining > 0 || isPremium;

  return (
    <div className={`min-h-screen bg-gradient-to-br ${gradients.background}`}>
      {deleteModal.show && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${cardStyles.default.background} rounded-3xl p-8 max-w-md w-full shadow-2xl`}>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Delete Session{deleteModal.sessionIds.length > 1 ? 's' : ''}?</h3>
            </div>
            <p className="text-gray-600 mb-6">
              This will permanently delete {deleteModal.sessionIds.length} session{deleteModal.sessionIds.length > 1 ? 's' : ''} and all associated data. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteModal({ show: false, sessionIds: [] })}
                disabled={deleting}
                className={`flex-1 ${buttonStyles.secondary.base} ${buttonStyles.secondary.hover} ${buttonStyles.secondary.active} ${buttonStyles.secondary.transition} ${buttonStyles.secondary.rounded} ${buttonStyles.secondary.shadow} py-3 px-6 disabled:opacity-50`}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteModal.sessionIds)}
                disabled={deleting}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-semibold py-3 px-6 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg"
              >
                {deleting ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showUpgradeModal && (
        <PremiumUpgradeModal 
          onClose={() => setShowUpgradeModal(false)}
          onNavigate={onNavigate}
          userProfile={userProfile}
        />
      )}

      <Header
  user={user}
  userProfile={userProfile}
  isPremium={isPremium}
  streakCount={streakCount}
  onMenuClick={handleMenuClick}  // ✅ Now using our handler
  onProgressClick={handleProgressClick}  // ✅ Now using our handler
  showProgress={!!user}
  sessionsText={
    isPremium || !limitEnabled 
      ? 'Unlimited sessions available' 
      : `${sessionsRemaining} of ${FREE_SESSION_LIMIT} free sessions remaining`
  }
/>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-8">
          {!isPremium && limitEnabled && (
            <div className="bg-gradient-to-r from-orange-50 to-peach-100 border border-orange-200 rounded-2xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">{sessionsRemaining}</span> of <span className="font-semibold">{FREE_SESSION_LIMIT}</span> free sessions remaining
                  </p>
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    className="text-sm text-orange-600 font-semibold hover:text-orange-700 mt-1"
                  >
                    Upgrade for unlimited access →
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <button
              onClick={canStartNewSession ? onStartNewSession : () => setShowUpgradeModal(true)}
              className={`flex-1 sm:flex-initial ${
                canStartNewSession 
                  ? `${buttonStyles.primary.base} ${buttonStyles.primary.hover} ${buttonStyles.primary.active} ${buttonStyles.primary.transition} ${buttonStyles.primary.rounded} ${buttonStyles.primary.shadow}`
                  : 'bg-gray-300 cursor-not-allowed rounded-xl'
              } text-white font-semibold py-3 px-6 flex items-center justify-center gap-2`}
            >
              <Sparkles className="w-5 h-5" />
              Start New Session
            </button>
            
            {sessions.length > 0 && (
              <button
                onClick={() => {
                  setSelectMode(!selectMode);
                  setSelectedSessions([]);
                }}
                className={`${buttonStyles.secondary.base} ${buttonStyles.secondary.hover} ${buttonStyles.secondary.active} ${buttonStyles.secondary.transition} ${buttonStyles.secondary.rounded} ${buttonStyles.secondary.shadow} py-3 px-6 flex items-center gap-2`}
              >
                {selectMode ? (
                  <>
                    <X className="w-5 h-5" />
                    Cancel
                  </>
                ) : (
                  <>
                    <Menu className="w-5 h-5" />
                    Select
                  </>
                )}
              </button>
            )}
            
            {selectMode && selectedSessions.length > 0 && (
              <button
                onClick={() => setDeleteModal({ show: true, sessionIds: selectedSessions })}
                className="bg-red-500 hover:bg-red-600 text-white font-semibold py-3 px-6 rounded-xl transition-all flex items-center gap-2 shadow-lg"
              >
                <Trash2 className="w-5 h-5" />
                Delete ({selectedSessions.length})
              </button>
            )}
          </div>
        </div>

        {renderInProgressSessions()}

        {completedSessions.length > 0 && (
          <div>
            <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Completed Sessions
            </h2>
            {renderCompletedSessions()}
          </div>
        )}

        {sessions.length === 0 && (
          <div className="text-center py-16">
            <div className="text-7xl mb-6">🌟</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">
              Start Your First Journey
            </h2>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Begin your manifestation practice with a personalized session. Each journey is unique to you.
            </p>
            <button
              onClick={onStartNewSession}
              className={`${buttonStyles.primary.base} ${buttonStyles.primary.hover} ${buttonStyles.primary.active} ${buttonStyles.primary.transition} ${buttonStyles.primary.rounded} ${buttonStyles.primary.shadow} py-4 px-8 flex items-center gap-2 mx-auto`}
            >
              <Sparkles className="w-5 h-5" />
              Start Your Journey
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionHistory;